import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, CheckCircle2, Banknote, Search } from "lucide-react";
import { api, apiError, money, fileUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { StatusPill } from "@/components/Pills";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = { merchant_id: "", amount: "", customer_id: "", upi_id: "", bank_name: "", bank_account_number: "", bank_account_holder_name: "", bank_ifsc_code: "", date: today() };

export default function PayoutRequest() {
  const [mode, setMode] = useState("new"); // "new" | "track"
  const [merchants, setMerchants] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const [cxId, setCxId] = useState("");
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState("");

  useEffect(() => { api.get("/public/merchants").then((r) => setMerchants(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.merchant_id) return toast.error("Please select a merchant");
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      const { data } = await api.post("/public/payout-request", { ...form, amount: amt });
      setDone(data);
      toast.success("Payout request submitted");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const loadEntries = async (e) => {
    e?.preventDefault();
    if (!cxId.trim()) return toast.error("Enter your Customer Reference (CX ID)");
    setLoading(true);
    try {
      const { data } = await api.get("/public/payouts", { params: { customer_id: cxId.trim() } });
      setEntries(data);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const act = async (id, kind) => {
    setActing(id + kind);
    try {
      await api.post(`/public/payouts/${id}/${kind}`, { customer_id: cxId.trim() });
      toast.success(kind === "approve" ? "Payout approved" : "Payout rejected — sent back to branch");
      loadEntries();
    } catch (err) { toast.error(apiError(err)); } finally { setActing(""); }
  };

  const F = ({ k, label, type = "text", required }) => (
    <div>
      <Label className="text-xs text-slate-500">{label}{required && <span className="text-rose-500"> *</span>}</Label>
      <Input data-testid={`pr-${k}-input`} type={type} value={form[k]} onChange={set(k)} className="mt-1 bg-white border-slate-300 text-slate-900" />
    </div>
  );

  const tab = (key, text) => (
    <button data-testid={`pr-tab-${key}`} onClick={() => setMode(key)}
      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${mode === key ? "bg-teal-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {text}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#F7F8FA] text-[#1F2937] font-sans px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-sm inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 mb-6"><ArrowLeft className="w-4 h-4" /> Home</Link>
        <div className="flex items-center gap-3 mb-6"><Logo /></div>

        <div className="flex gap-2 mb-5">{tab("new", "Request Payout")}{tab("track", "Track & Approve")}</div>

        {mode === "new" && (done ? (
          <div data-testid="pr-success" className="rounded-2xl border border-emerald-200 bg-white shadow-sm p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h1 className="text-xl font-bold mt-3 text-slate-900">Payout request submitted</h1>
            <p className="text-sm text-slate-500 mt-1">Status: <span className="font-semibold">{done.status}</span></p>
            <p className="text-xs text-slate-400 mt-1 font-mono">Ref: {done.id}</p>
            <p className="text-sm text-slate-600 mt-4">Once the branch pays you, come back to <button onClick={() => { setMode("track"); setCxId(form.customer_id); }} className="text-teal-600 underline font-medium">Track &amp; Approve</button> using your Customer Reference to confirm receipt.</p>
            <Button className="mt-5 bg-teal-600 hover:bg-teal-700 text-white" data-testid="pr-new-button" onClick={() => { setDone(null); setForm(emptyForm); }}>Submit another</Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
            <div className="flex items-center gap-2 text-teal-600"><Banknote className="w-5 h-5" /><h1 className="text-xl font-bold tracking-tight text-slate-900">Request a Payout</h1></div>
            <p className="text-sm text-slate-500 mt-1">Submit your payout details. Our team will process the request.</p>
            <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500">Merchant <span className="text-rose-500">*</span></Label>
                <select data-testid="pr-merchant-select" value={form.merchant_id} onChange={set("merchant_id")} className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900">
                  <option value="">Select a merchant…</option>
                  {merchants.map((m) => <option key={m.id} value={m.id}>{`${m.name} (${m.merchant_code})`}</option>)}
                </select>
              </div>
              <F k="amount" label="Amount (₹)" type="number" required />
              <F k="customer_id" label="Customer Reference (CX ID)" required />
              <F k="upi_id" label="UPI ID" />
              <F k="bank_name" label="Bank Name" />
              <F k="bank_account_number" label="Bank Account Number" />
              <F k="bank_account_holder_name" label="Account Holder Name" />
              <F k="bank_ifsc_code" label="IFSC Code" />
              <div className="sm:col-span-2">
                <Button data-testid="pr-submit-button" type="submit" disabled={busy} className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Payout Request"}
                </Button>
              </div>
            </form>
          </div>
        ))}

        {mode === "track" && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
            <div className="flex items-center gap-2 text-teal-600"><Search className="w-5 h-5" /><h1 className="text-xl font-bold tracking-tight text-slate-900">Track &amp; Approve</h1></div>
            <p className="text-sm text-slate-500 mt-1">Enter your Customer Reference to view your payouts and approve once paid.</p>
            <form onSubmit={loadEntries} className="mt-5 flex gap-2">
              <Input data-testid="pr-lookup-input" value={cxId} onChange={(e) => setCxId(e.target.value)} placeholder="Customer Reference (CX ID)" className="bg-white border-slate-300 text-slate-900" />
              <Button data-testid="pr-lookup-button" type="submit" disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Look up"}</Button>
            </form>

            {entries && entries.length === 0 && <p className="text-sm text-slate-400 mt-6 text-center" data-testid="pr-no-entries">No payouts found for this reference.</p>}

            <div className="mt-5 space-y-3">
              {(entries || []).map((p) => (
                <div key={p.id} data-testid={`pr-entry-${p.id}`} className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-black font-mono text-slate-900">{money(p.amount)}</div>
                    <StatusPill status={p.status} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{p.merchant_name || p.merchant_id} · {p.date}</div>
                  {p.transaction_id && <div className="text-xs text-slate-500 mt-0.5">TX ID: <span className="font-mono">{p.transaction_id}</span></div>}
                  {p.status === "Paid - Pending Approval" && (
                    <div className="mt-3 space-y-2">
                      {p.screenshot && <a href={fileUrl(p.screenshot)} target="_blank" rel="noreferrer" className="text-teal-600 underline text-xs">View payment screenshot</a>}
                      <div className="flex gap-2">
                        <Button data-testid={`pr-approve-${p.id}`} disabled={acting === p.id + "approve"} onClick={() => act(p.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9">Approve</Button>
                        <Button data-testid={`pr-reject-${p.id}`} disabled={acting === p.id + "reject"} onClick={() => act(p.id, "reject")} variant="outline" className="h-9 border-rose-200 text-rose-600 hover:bg-rose-50">Reject</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
